/**
 * The project and management reports (docs/25 — the reference's
 * Profitability, Cost variance, Billing reconciliation, Regularise BOQ,
 * Financials and Document approval tracker).
 *
 * Kept in a file of their own: they share one way of reading a project's
 * money (the server's project ledger) and a vocabulary — estimated,
 * committed (ordered, not yet invoiced), incurred, to complete — that the
 * rest of the reports do not.
 */
import { defineReport, rc, rf, type ReportColumnDef, type ReportFilterDef } from './types.js'
import { DOCUMENTS } from '../documents/registry.js'

/**
 * The order the reference lists document types in (REP1-013): the purchasing
 * chain first, billing after, RFQ and quotations last.
 */
const APPROVAL_DOC_ORDER = [
  'project', 'material_request', 'purchase_order', 'goods_receipt', 'delivery_challan', 'purchase_invoice',
  'material_transfer', 'subcontract_order', 'work_progress', 'other_cost', 'billing_request', 'sales_invoice',
  'credit_note', 'expense_report', 'site_inventory_record', 'stock_adjustment', 'request_for_quote', 'supplier_quotation',
]
const approvalRank = (key: string): number => {
  const i = APPROVAL_DOC_ORDER.indexOf(key)
  return i < 0 ? APPROVAL_DOC_ORDER.length : i
}

/**
 * Every document type that goes through a workflow — which is all of them
 * (RPT-044, RPT-046) — in the reference's order; ours it does not list follow
 * in registry order.
 */
export const APPROVAL_DOC_TYPE_OPTIONS = DOCUMENTS
  .filter((d) => Boolean(d.workflow))
  .map((d, i) => ({ value: d.key, label: d.label, i }))
  .sort((a, b) => approvalRank(a.value) - approvalRank(b.value) || a.i - b.i)
  .map(({ value, label }) => ({ value, label }))

const qtyIn = (key: string, label: string, group: string, total = false): ReportColumnDef =>
  ({ ...rc.qty(key, label, total), group })
const rateIn = (key: string, label: string, group: string): ReportColumnDef =>
  ({ key, label, type: 'rate', width: 118, align: 'right', group })
const moneyIn = (key: string, label: string, group: string): ReportColumnDef =>
  ({ ...rc.money(key, label), group })
const pctBadge = (key: string, label: string, badge: ReportColumnDef['badge'] = 'signed'): ReportColumnDef =>
  ({ ...rc.pct(key, label), badge })
/** The Details button on every row: the row's own drill (the chassis `details` column). */
const detailsColumn = (): ReportColumnDef => ({ key: 'details', label: 'Details', type: 'details', width: 104, filter: false })

const COST_DOCS = ['purchase_order', 'subcontract_order', 'purchase_invoice', 'other_cost', 'expense_report', 'material_transfer']
const INCURRED_DOCS = ['purchase_invoice', 'other_cost', 'expense_report', 'material_transfer']
const COMMITTED_DOCS = ['purchase_order', 'subcontract_order']
const PROGRESS_DOCS = ['goods_receipt', 'purchase_invoice', 'material_transfer', 'work_progress', 'delivery_challan']

/* ================= Management ================= */

/**
 * The only default Financials columns somebody may take off (REP1-009); every
 * other default figure is fixed, as in the reference's column chooser.
 */
const FINANCIALS_REMOVABLE = ['masterProject', 'customerPo', 'customerPoDate', 'ntValue']

export const projectFinancials = defineReport({
  key: 'project_financials', route: 'project_management_dashboard', group: 'Management',
  label: 'Financials — ongoing projects',
  blurb: "A bird's eye view of every open project's value, billing and cost.",
  filters: [
    rf.customer(), rf.jobType(),
    { ...rf.period('Document date', false), defaultPeriod: 'none' },
    { key: 'employeeId', label: 'Project manager', control: 'lookup', lookup: { entity: 'employee' } },
  ],
  columns: ([
    { ...rc.text('customerName', 'Customer', 220), frozen: true },
    { ...rc.doc('projectNo', 'Project'), link: { to: 'project', idKey: 'id' }, frozen: true, locked: true },
    { ...rc.text('projectName', 'Project name', 220), frozen: true },
    rc.text('masterProject', 'Master project', 200),
    rc.text('customerPo', 'Customer PO', 150), rc.date('customerPoDate', 'Customer PO date'),
    rc.money('projectValue', 'Project value'),
    rc.money('unbilledSupply', 'Unbilled — supply'), rc.money('unbilledInstallation', 'Unbilled — installation'),
    rc.money('unbilledTotal', 'Unbilled — total'),
    rc.money('billed', 'Billed value'), pctBadge('billedPct', 'Billed %', 'good'),
    rc.money('openValue', 'Open value'), rc.pct('openPct', 'Open %'),
    rc.money('estimatedCost', 'Estimated cost'), rc.money('cost', 'Costs incurred'),
    pctBadge('costPct', 'Costs incurred %', 'warn'),
    rc.signed('costVariance', 'Cost variance'), pctBadge('costVariancePct', 'Cost variance %'),
    rc.money('ntValue', 'NT value'),
    rc.signed('billingSurplus', 'Billing surplus'), pctBadge('billingSurplusPct', 'Billing surplus %'),
    pctBadge('projectedMarginPct', 'Projected margin %'),
    rc.money('ber', 'BER (overbilled)'), { ...rc.money('reb', 'REB (to be billed)'), badge: 'bad' },
    { ...rc.text('projectManager', 'Project manager', 180), optional: true },
    { ...rc.date('documentDate', 'Document date'), optional: true },
    { ...rc.text('projectStage', 'Project stage', 150), optional: true, filter: 'pick' },
    { ...rc.money('committedCost', 'Committed cost'), optional: true },
    { ...rc.pct('committedPct', 'Committed cost %'), optional: true },
    { ...rc.text('jobType', 'Job type', 160), optional: true, filter: 'pick' },
    { ...rc.money('requested', 'Billing requested'), optional: true },
  ] satisfies ReportColumnDef[]).map((c): ReportColumnDef => (c.optional || FINANCIALS_REMOVABLE.includes(c.key) ? c : { ...c, locked: true })),
  drill: { by: 'project', key: 'id', label: 'Documents on this project' },
  help: [
    'Only ongoing projects are listed; a project marked closed moves to Profitability — completed projects.',
    'Project value is the approved BOQ on its order quantities. Billed value is approved sales invoices less credit notes, before tax. Open value is project value less billed.',
    'Unbilled is work done but not yet billed, BOQ line by BOQ line: the supply half done (receipts, transfers, direct invoices) and the installation half done (work progress), less what has been billed against the line. REB is all the work done not yet billed; BER is what has been billed beyond the work done.',
    'Estimated cost is the estimation lines. Costs incurred is approved purchase invoices, other costs and expense reports, less material returned to store. Committed is ordered and not yet invoiced. Cost variance is Estimated − (Committed + Incurred + To complete), where To complete is the estimated quantity nothing has been ordered or invoiced against yet, at the estimated rate.',
    'Billing surplus is billed less incurred. Projected margin is project value less projected cost (committed + incurred + to complete), over project value. NT value is the BOQ lines marked non tendered.',
  ].join('\n\n'),
} as const)

export const completedProfitability = defineReport({
  key: 'completed_profitability', route: 'completed_projects_profit', group: 'Management',
  label: 'Profitability — completed projects',
  blurb: 'Profit on projects that are finished, so the estimate can be checked against reality.',
  filters: [
    { ...rf.period('Completed date'), defaultPeriod: 'last12Months' }, rf.customer(),
    { key: 'employeeId', label: 'Project manager', control: 'lookup', lookup: { entity: 'employee' }, multiple: true },
  ],
  columns: [
    { ...rc.doc('projectNo', 'Project'), link: { to: 'project', idKey: 'id' } }, rc.text('projectName', 'Name'),
    rc.date('completedOn', 'Completed'),
    rc.money('billed', 'Billed'), rc.money('cost', 'Cost'),
    rc.signed('margin', 'Profit'), pctBadge('marginPct', 'Margin %'),
  ],
  drill: { by: 'project', key: 'id', label: 'Documents on this project' },
  help: 'A project counts as completed once it is marked closed (not ongoing), or once every task on its schedule has an actual finish. The completed date is the last actual finish on the schedule, else the day it was closed.',
} as const)

/**
 * One workflow step block. Only the steps the result reaches are drawn: a
 * two-step workflow shows Workflow step 1 and 2 and no empty third (REP1-012).
 */
const step = (n: number): ReportColumnDef[] => {
  const group = `Workflow step ${n}`
  const hideWhenEmpty = true
  return [
    { ...rc.text(`step${n}`, 'Step', 150), group, hideWhenEmpty },
    { ...rc.text(`step${n}By`, 'Approved by', 170), group, hideWhenEmpty },
    { ...rc.text(`step${n}At`, 'Approved time', 170), group, hideWhenEmpty },
    { key: `step${n}Took`, label: 'Time taken', type: 'duration', width: 130, align: 'right', total: true, group, hideWhenEmpty },
  ]
}

/** Opens on In review: what is waiting for approval, across every type (REP1-001). */
const approvalStatus: ReportFilterDef = {
  key: 'documentStatus', label: 'Document status', control: 'select', required: true, defaultValue: 'IN_REVIEW',
  options: [
    { value: 'ALL', label: 'All' }, { value: 'IN_REVIEW', label: 'In review' }, { value: 'DRAFT', label: 'Draft' },
    { value: 'APPROVED', label: 'Approved' }, { value: 'CANCELLED', label: 'Cancelled' },
  ],
}

export const approvalTracker = defineReport({
  key: 'approval_tracker', route: 'doc_approval_tracker', group: 'Management',
  label: 'Document approval tracker',
  blurb: 'Who submitted each document, who approved each step, and how long every step took.',
  filters: [
    approvalStatus,
    // Every type when looking at one status; one type at least when looking at all of them.
    { ...rf.docType(APPROVAL_DOC_TYPE_OPTIONS), requiredWhen: { key: 'documentStatus', values: ['ALL'] } },
    { ...rf.period('Document date'), defaultPeriod: 'last3Months' }, rf.project(),
  ],
  columns: [
    { ...rc.text('docTypeLabel', 'Document type', 190), frozen: true, filter: 'pick' }, { ...rc.doc(), frozen: true },
    rc.date('documentDate', 'Document date'),
    rc.text('submittedBy', 'Last submitted by', 170), rc.text('submittedAt', 'Last submitted time', 170),
    rc.status(), rc.text('currentStage', 'Current step', 170),
    ...step(1), ...step(2), ...step(3), ...step(4), ...step(5),
    { key: 'totalTime', label: 'Total approval time', type: 'duration', width: 150, align: 'right', total: true },
    { key: 'waitingFor', label: 'Waiting for', type: 'duration', width: 140, align: 'right', total: true, optional: true },
    rc.project('projectName', 'Project'),
    { ...rc.text('jobType', 'Job types', 160), filter: 'pick' },
  ],
  help: 'Read from the approval history: the last submission, then each step signed after it, with who signed and how long the document waited for that step. Total approval time runs from the last submission to the final approval, or to now while it is still in review.',
} as const)

/* ================= Project ================= */

export const profitAnalysis = defineReport({
  key: 'profit_analysis', route: 'profit_analysis', group: 'Project',
  label: 'Profitability analysis',
  blurb: 'What one project was estimated to make, what it has made so far, and where it is heading.',
  filters: [rf.project(true), rf.includeSub(), rf.asOn()],
  columns: [
    { ...rc.text('boqRef', 'BOQ ref no', 110), frozen: true },
    { ...rc.text('boqName', 'BOQ', 240), frozen: true },
    rc.text('unit', 'Unit', 90),
    rc.qty('orderQty', 'Order qty'), rc.qty('designQty', 'Design qty'),
    { key: 'boqRate', label: 'BOQ rate', type: 'rate', width: 118, align: 'right' },
    rc.money('orderValue', 'Order value'), rc.money('designValue', 'Project value (based on design)'),
    rc.money('billedValue', 'Billed value'),
    rc.money('estimatedCost', 'Estimated cost'),
    { ...rc.money('committedCost', 'Committed cost'), drill: { docTypes: COMMITTED_DOCS, label: 'What is committed' } },
    { ...rc.money('incurredCost', 'Incurred cost'), drill: { docTypes: INCURRED_DOCS, label: 'What was incurred' } },
    rc.money('projectedCost', 'Projected cost'),
    rc.signed('billingSurplus', 'Billing surplus'),
    rc.signed('projectedProfit', 'Projected profit'), rc.signed('actualProfit', 'Actual profit'),
    pctBadge('billingSurplusPct', 'Billing surplus %'), pctBadge('profitPct', 'Projected/actual profit %'),
  ],
  drill: { by: 'document', key: 'rowKey', docTypes: COST_DOCS, label: 'Cost on this BOQ line' },
  hasSummaryDetail: true,
  summary: {
    layout: 'cards', rowKey: 'stage',
    cards: [
      // The order value rides as a chip on the project value (REP1-010).
      { title: 'Original (based on design)', metrics: [
        { label: 'Project value', row: 'Original', key: 'value', type: 'money', chip: { key: 'orderValue', type: 'money' } },
        { label: 'Estimated cost', row: 'Original', key: 'cost', type: 'money' },
        { label: 'Estimated profit', row: 'Original', key: 'profit', type: 'money', signed: true },
        { label: 'Estimated profit margin', row: 'Original', key: 'marginPct', type: 'pct' },
      ] },
      { title: 'Revised (based on design)', metrics: [
        { label: 'Project value', row: 'Revised', key: 'value', type: 'money', chip: { key: 'orderValue', type: 'money' } },
        { label: 'Estimated cost', row: 'Revised', key: 'cost', type: 'money' },
        { label: 'Estimated profit', row: 'Revised', key: 'profit', type: 'money', signed: true },
        { label: 'Estimated profit margin', row: 'Revised', key: 'marginPct', type: 'pct' },
      ] },
      { title: 'Current (based on design)', metrics: [
        { label: 'Billed value', row: 'Current', key: 'value', type: 'money' },
        { label: 'Cost incurred', row: 'Current', key: 'cost', type: 'money' },
        { label: 'Billing surplus', row: 'Current', key: 'profit', type: 'money', signed: true },
        { label: 'Billing surplus margin', row: 'Current', key: 'marginPct', type: 'pct' },
      ] },
      { title: 'Projected/actual (based on design)', metrics: [
        { label: 'Billed value', row: 'Projected', key: 'value', type: 'money' },
        { label: 'Projected/actual cost', row: 'Projected', key: 'cost', type: 'money' },
        { label: 'Projected/actual profit', row: 'Projected', key: 'profit', type: 'money', signed: true },
        { label: 'Projected/actual profit margin', row: 'Projected', key: 'marginPct', type: 'pct' },
      ] },
    ],
  },
  help: [
    'Original is the project as first approved (revision 0); Revised is the approved project now. Both are valued on the design quantities, with the order value beside them, against the estimation lines.',
    'Current is what has been billed (approved sales invoices less credit notes, before tax) against the cost incurred (approved purchase invoices, other costs and expense reports, less material returned to store); the difference is the billing surplus.',
    'Projected/actual: while the project is ongoing, the value it will bill on its design quantities against the projected cost — committed (ordered, not yet invoiced) + incurred + the estimated quantity still to complete. Once it is closed, what was billed against what was incurred.',
    'Detailed lists every BOQ line, with a last line for common items and overheads: estimation lines that belong to no BOQ line, and cost that no estimation line covers. As on date counts only documents dated on or before it.',
  ].join('\n\n'),
} as const)

export const costVariance = defineReport({
  key: 'cost_variance', route: 'cost_variance', group: 'Project',
  label: 'Cost variance analysis',
  blurb: 'Estimate against committed, incurred and still to complete, estimation line by estimation line.',
  filters: [rf.project(true), rf.includeSub(), rf.asOn()],
  columns: [
    { ...rc.text('boqRef', 'BOQ ref no', 110), frozen: true },
    { ...rc.text('boqName', 'BOQ', 220), frozen: true },
    rc.text('estimationLine', 'Estimation line', 120),
    rc.text('description', 'Description', 220), rc.text('unit', 'Unit', 90),
    qtyIn('estQty', 'Qty', 'Estimated'), rateIn('estRate', 'Rate', 'Estimated'), moneyIn('estimated', 'Amount', 'Estimated'),
    qtyIn('comQty', 'Qty', 'Committed'), rateIn('comRate', 'Rate', 'Committed'),
    { ...moneyIn('committed', 'Amount', 'Committed'), drill: { docTypes: COMMITTED_DOCS, label: 'What is committed' } },
    qtyIn('incQty', 'Qty', 'Incurred'), rateIn('incRate', 'Rate', 'Incurred'),
    { ...moneyIn('incurred', 'Amount', 'Incurred'), drill: { docTypes: INCURRED_DOCS, label: 'What was incurred' } },
    qtyIn('openQty', 'Qty', 'Open'), rateIn('openRate', 'Rate', 'Open'), moneyIn('open', 'Amount', 'Open'),
    { ...rc.signed('variance', 'Variance'), drill: { docTypes: COST_DOCS, label: 'Cost variance — detailed' } },
    pctBadge('variancePct', 'Variance %'),
    { ...rc.text('costType', 'Type', 150), filter: 'pick' },
    detailsColumn(),
  ],
  hasSummaryDetail: true,
  summary: {
    layout: 'cards', rowKey: 'costType', totalRow: 'Total', breakup: true,
    // Three untitled tiles as the reference draws them, the % beside the variance (REP1-011).
    breakupTitle: 'Cost variance - breakup',
    cards: [
      { title: '', metrics: [
        { label: 'Estimated - original', row: 'Total', key: 'estimatedOriginal', type: 'money' },
        { label: 'Estimated - revised', row: 'Total', key: 'estimated', type: 'money' },
        { label: 'Projected', row: 'Total', key: 'projected', type: 'money' },
      ] },
      { title: '', metrics: [
        { label: 'Committed', row: 'Total', key: 'committed', type: 'money' },
        { label: 'Incurred', row: 'Total', key: 'incurred', type: 'money' },
        { label: 'Open', row: 'Total', key: 'open', type: 'money' },
      ] },
      { title: '', metrics: [
        { label: 'Variance', row: 'Total', key: 'variance', type: 'money', signed: true,
          chip: { key: 'variancePct', type: 'pct', signed: true } },
      ] },
    ],
    breakupColumns: [
      { ...rc.text('costType', 'Type', 200), filter: false },
      rc.money('estimated', 'Estimated'), rc.money('committed', 'Committed'), rc.money('incurred', 'Incurred'),
      rc.money('projected', 'Projected'), rc.signed('variance', 'Variance'), pctBadge('variancePct', 'Variance %'),
    ],
  },
  drill: { by: 'document', key: 'rowKey', docTypes: COST_DOCS, label: 'Cost variance — detailed' },
  help: [
    'Every estimation line, with what has been committed against it (ordered and not yet invoiced), incurred (approved purchase invoices, other costs and expense reports, less returns to store) and what is still open — the estimated quantity nothing has been ordered or invoiced against, at the estimated rate.',
    'Variance = Estimated − (Committed + Incurred + To complete). Positive is under the estimate. Variance % is against the estimate.',
    'Cost is charged to the estimation line it was raised from, following the documents back: invoice → receipt → order → material request → estimation line. Cost that reaches no estimation line is listed by item as not estimated.',
    'Original is the estimate as first approved; Revised is the approved estimate now. As on date counts only documents dated on or before it.',
  ].join('\n\n'),
} as const)

export const regulariseBoq = defineReport({
  key: 'regularise_boq', route: 'regularise_boq', group: 'Project',
  label: 'Regularise BOQ',
  blurb: 'Estimation lines where more has been done than was estimated — what to regularise with the customer.',
  filters: [
    rf.project(true), rf.includeSub(),
    { key: 'boqStatus', label: 'BOQ status', control: 'select', defaultValue: 'ALL', options: [
      { value: 'ALL', label: 'All' }, { value: 'TENDERED', label: 'Tendered' }, { value: 'NON_TENDERED', label: 'Non tendered' },
    ] },
    // Billable lines, alerts and warnings: what the reference runs on (REP1-004).
    { key: 'billable', label: 'Type', control: 'select', defaultValue: 'BILLABLE', options: [
      { value: 'ALL', label: 'All' }, { value: 'BILLABLE', label: 'Billable' }, { value: 'NON_BILLABLE', label: 'Non billable' },
    ] },
    { key: 'show', label: 'Show', control: 'select', defaultValue: 'ALL', options: [
      { value: 'ALERTS', label: 'Only alerts' }, { value: 'ALL', label: 'Alerts and warnings' },
    ] },
  ],
  columns: [
    { ...rc.text('boqRef', 'BOQ ref no', 110), frozen: true },
    // The BOQ's own cells on its first estimation line only (REP1-008).
    { ...rc.text('boqStatus', 'BOQ status', 130), filter: 'pick', oncePer: 'boqLineId' },
    { ...rc.text('boqName', 'BOQ', 220), oncePer: 'boqLineId' },
    { ...rc.qty('orderQty', 'Order qty'), oncePer: 'boqLineId' }, { ...rc.qty('designQty', 'Design qty'), oncePer: 'boqLineId' },
    { ...rc.text('boqUnit', 'BOQ unit', 100), oncePer: 'boqLineId' },
    { ...rc.text('type', 'Type', 130), filter: 'pick' },
    rc.text('itemName', 'Item', 220), rc.text('unit', 'Unit', 90),
    rc.qty('estimatedQty', 'Estimated qty'),
    { ...rc.qty('completedQty', 'Completed qty'), drill: { docTypes: PROGRESS_DOCS, label: 'Completed quantity details' } },
    rc.money('amount', 'Amount'),
    { ...rc.text('category', 'Item category', 160), filter: 'pick' },
    { ...rc.text('itemType', 'Item type', 130), filter: 'pick' },
    { ...rc.text('severityLabel', 'Flag', 110), filter: 'pick' },
    { ...rc.qty('excessQty', 'Excess qty'), optional: true },
  ],
  rowTone: 'severity',
  drill: { by: 'document', key: 'estimationLineId', docTypes: PROGRESS_DOCS, label: 'Completed quantity details' },
  help: [
    'One row per estimation line; the BOQ\'s own cells (status, BOQ, order and design quantity, unit) are shown on its first estimation line only, so the lines of one BOQ read as a group. Completed is what has actually happened against it — the first receipt, transfer, challan, direct invoice or work progress on each chain of documents. Amount is the completed quantity at the estimated rate.',
    'An alert is an estimation line whose completed quantity is above its estimate: work the customer has not ordered, to be regularised. A warning is one at 90% of its estimate or more, a non tendered BOQ line, or a line whose design quantity differs from its order quantity.',
  ].join('\n\n'),
} as const)

export const billingReconciliation = defineReport({
  key: 'billing_reconciliation', route: 'billing_reconcile', group: 'Project',
  label: 'Billing reconciliation',
  blurb: 'Work completed against what has been billed, billing line by billing line.',
  filters: [rf.project(true), rf.includeSub(), rf.asOn()],
  columns: [
    { ...rc.text('boqRef', 'BOQ ref no', 110), frozen: true },
    { ...rc.text('boqName', 'BOQ', 220), frozen: true },
    rc.text('billingLine', 'Billing line', 220),
    rc.qty('designQty', 'Design qty'), rc.text('unit', 'Unit', 90),
    { key: 'boqRate', label: 'BOQ rate', type: 'rate', width: 118, align: 'right' },
    pctBadge('billingPct', 'Billing %', 'good'),
    { key: 'billingRate', label: 'Billing rate', type: 'rate', width: 118, align: 'right' },
    rc.money('amount', 'Amount'),
    qtyIn('completedQty', 'Quantity', 'Completed'), moneyIn('completedAmount', 'Amount', 'Completed'),
    qtyIn('billedQty', 'Quantity', 'Already billed'),
    { ...moneyIn('billedAmount', 'Amount', 'Already billed'), drill: { docTypes: ['billing_request', 'sales_invoice'], label: 'Bills' } },
    qtyIn('unbilledQty', 'Quantity', 'Unbilled'), moneyIn('unbilledAmount', 'Amount', 'Unbilled'),
    detailsColumn(),
  ],
  drill: { by: 'document', key: 'boqLineId', label: 'BOQ details' },
  hasSummaryDetail: true,
  summary: {
    layout: 'cards',
    donut: { title: 'Project value', segments: [
      { label: 'Unbilled', key: 'unbilledAmount' }, { label: 'Already billed', key: 'billedAmount' },
      { label: 'Open value', key: 'openValue' },
    ] },
    cards: [
      { title: 'Project value (based on design)', metrics: [
        { label: 'Project value', key: 'projectValue', type: 'money', chip: { key: 'orderValue', type: 'money' } },
        { label: 'Unbilled (excludes NT extra items)', key: 'unbilledAmount', type: 'money' },
        { label: 'Already billed', key: 'billedAmount', type: 'money' },
        { label: 'Open value', key: 'openValue', type: 'money' },
      ] },
      // Total cost against the estimated cost, as a bar (REP1-006, PROJ1-031).
      { title: 'Cost', metrics: [
        { label: 'Supplier invoiced', key: 'supplierInvoiced', type: 'money' },
        { label: 'Material TFR', key: 'materialTransfer', type: 'money' },
        { label: 'Expense report', key: 'expenseReport', type: 'money' },
        { label: 'Other cost', key: 'otherCost', type: 'money' },
        { label: 'Total cost', key: 'totalCost', type: 'money', progress: { pctKey: 'costPct', targetKey: 'estimatedCost' } },
      ] },
      // The qualifier is per figure: NT extra items are cost, not unbilled (REP1-007).
      { title: '', metrics: [
        { label: 'Rate only BOQ (included in unbilled)', key: 'roValue', type: 'money' },
        { label: 'Excess BOQ quantity (included in unbilled)', key: 'excessValue', type: 'money' },
        { label: 'Non tendered - extra items (incurred cost)', key: 'ntIncurred', type: 'money' },
      ] },
    ],
  },
  help: [
    'One row per billing line: each BOQ line is split by the lines of its billing term (65% on supply, 25% on installation…). Billing rate is the BOQ rate (or its supply or installation half) times the line’s percentage, and Amount is the design quantity at that rate.',
    'Completed is the design quantity times how far the BOQ line has got — measured on its supply estimates for a line released by supply, its installation estimates for one released by installation, and all its billable estimates otherwise. A line nothing measures is taken as complete. Already billed is approved billing requests and invoices against the BOQ line; Unbilled is completed less already billed.',
    'Summary: Open value is the project value not yet completed. Total cost is measured against the estimated cost (the estimation lines), the bar\'s target. Excess BOQ quantity is work done beyond the design quantity; Non tendered extra items are BOQ lines outside the tender, at what they have cost so far. As on date counts only documents dated on or before it.',
  ].join('\n\n'),
} as const)
